import React, { useCallback, useState } from 'react';

import { ButtonGroup, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { updateJobState, purgeJob, downloadJob } from '../../apis/jobApi';
import { usePermissionContext } from '../../auth/AuthContext';
import { useDownloadFileProgress } from '../../context/DownloadFileContext';
import { 
    ExclamationCircle, 
    PlayFill, 
    PauseFill, 
    StopFill, 
    Dash, 
    Trash, 
    Archive,
    Upload,
    Download
} from 'react-bootstrap-icons';
import { FilesUpload } from '../Files/FilesUpload';


const JobStateAction = ({jobData, handleJobStateChange, hasStateChangePermission, onUploadClick}) => 
{
    if(jobData === undefined)
    {
        return <Button name="undefinedState" className="jobActionAlert button-table" aria-label="Unknown Job State"><ExclamationCircle/></Button>;
        
    }
    else
    {
        const jobState = jobData["state"];
        switch(jobState){
            case "Draft":
                return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Upload Files</Tooltip>}>
                    <Button disabled={!hasStateChangePermission} className="jobActionUpload button-table button-icon" name = "upload" aria-label="Upload Files" onClick={onUploadClick ? onUploadClick : () => handleJobStateChange("upload")}><Upload/></Button>
                </OverlayTrigger>;
            case "Paused":
                return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Start Job</Tooltip>}>
                    <Button disabled={!hasStateChangePermission} className="jobActionStart button-table button-icon" name = "start" aria-label="Start Job" onClick={() => handleJobStateChange("start")}><PlayFill/></Button>
                </OverlayTrigger>;
            case "Pausing":
                return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Start Job</Tooltip>}>
                    <Button disabled className="jobActionStart button-table button-icon" name = "start" aria-label="Start Job Disabled" onClick={() => handleJobStateChange("start")}><PlayFill/></Button>
                </OverlayTrigger>;
            case "Queueing":
            case "Queued":
                return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Pause Job</Tooltip>}>
                    <Button disabled={!hasStateChangePermission} className="jobAction button-table" name = "pause" aria-label="Pause Job" onClick={() => handleJobStateChange("pause")}><PauseFill/></Button>
                </OverlayTrigger>;
            case "Stopping":
                return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Stop Job</Tooltip>}>
                    <Button disabled={!hasStateChangePermission} className="jobActionPaused  button-table button-icon" name="stop" aria-label="Stop Job" kind="ghost"><StopFill/></Button>
                </OverlayTrigger>   ;
            case "Stopped":
            case "Completed":
            case "Failed":
            case "Archived":
            case "Purging":
            case "Purged":
                return <Button disabled className="jobActionDisabled button-table button-icon" name = "start" aria-label="No action available" kind="ghost"><Dash/></Button>;
            default:
                return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Pause Job</Tooltip>}>
                    <Button disabled={!hasStateChangePermission} className="jobAction  button-table  button-icon" name="pause" aria-label="Pause Job" onClick={() => handleJobStateChange("pause")}><PauseFill/></Button>
                </OverlayTrigger>;
        }   
    }
}


const PurgeJob  = ({jobData, handlePurgeJob}) =>
{
    const { hasRole } = usePermissionContext();


    if(jobData === undefined )
    {
        return <Button className="button-table" variant="primary" disabled aria-label="Purge Job"><Trash/></Button>
    }
    else
    {
        return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Purge Job</Tooltip>}>
            <Button disabled={hasRole('Admin')} className="button-table" variant="primary" aria-label="Purge Job" onClick={handlePurgeJob}><Trash/></Button>
        </OverlayTrigger>;

    }
}

const ArchiveJob = ({jobData, handleArchiveJob, hasArchivePermission}) =>
{
    const { hasRole } = usePermissionContext();
    
    if(jobData === undefined )
    {
        return <Button disabled className="button-table" variant="primary" aria-label="Archive Job"><Archive/></Button>
    }
    else
    {
        return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Archive Job</Tooltip>}>
            <Button disabled={!hasArchivePermission} className="button-table  button-icon" variant="primary" aria-label="Archive Job" onClick={handleArchiveJob}><Archive/></Button>
        </OverlayTrigger>;

    }
}

const DownloadJob = ({jobData, handleDownloadJob}) =>
{
    if(jobData === undefined)
    {
        return <Button disabled className="button-table button-icon" aria-label="Download Job"><Download/></Button>
    }
    else
    {
        return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Download Job</Tooltip>}>
            <Button className="button-table button-icon" variant="primary" aria-label="Download Job" onClick={handleDownloadJob}><Download/></Button>
        </OverlayTrigger>;
    }
}

export const JobsRowActions = (rowProps) => {
    const setErrorMessage = rowProps.setErrorMessage;
    const { hasRole } = usePermissionContext();
    const { startDownload, updateProgress, updateErrorDownloads } = useDownloadFileProgress();

    const [showUploadModal, setShowUploadModal] = useState(false);
    const handleUploadClick = useCallback(() => setShowUploadModal(true), []);
    const handleUploadClose = useCallback(() => setShowUploadModal(false), []);
    const [ hasStateChangePermission, setHasStateChangePermission] = useState(true);
    const [ hasArchivePermission, setHasArchivePermission] = useState(hasRole('Admin'));
    const handleJobStateChange =  async(newJobState) => {        
        try
        {
            const gridRef = rowProps.gridRef;
            const response = await updateJobState(rowProps.data.id, newJobState);
            rowProps.node.data = response
            gridRef.current.api.refreshCells({rowNodes: [rowProps.node], force: true, columnIds: ["state"]}); 
            
        }
        catch(error)
        {
            if(error.status === 403)
            {
                setHasStateChangePermission(false);
                setErrorMessage({alertType:"warning", message: 'You do not have permission to perform this action. Please contact your administrator.'});
            }
            else if (error.status === 400)
            {
                setHasStateChangePermission(false);
                setErrorMessage({alertType:"danger", message: `Error updating job state: ${error.detail.detail}`});
            }
            else
            {
                setHasStateChangePermission(false);
                setErrorMessage({alertType:"danger", message: `Error updating job state: ${error.message}`});
            }
        }
    }

    const handleArchiveJob = useCallback(async () => 
    {
        try
        {
            const response = await updateJobState(rowProps.data.id, "archive");
            if(response.error)
            {
                setHasArchivePermission(false);
                setErrorMessage({alertType:"danger", message: `Error archiving job: ${response.message}`});
            }
            else if (response.status === 400)
            {
                setHasArchivePermission(false);
                setErrorMessage({alertType:"danger", message: `Error archiving job: ${response.detail}`});
            }
            else
            {
                rowProps.node.data = response
                rowProps.gridRef.current.api.refreshCells({rowNodes: [rowProps.node], force: true, columnIds: ["state"]});
            }    
        }
        catch(error){
            if(error.status === 403)
            {
                setHasArchivePermission(false);
                setErrorMessage({alertType:"warning", message: 'You do not have permission to perform this action. Please contact your administrator.'});
            }
            else if(error.status === 400)
            {
                setHasArchivePermission(false);
                setErrorMessage({alertType:"danger", message: `Error archiving job: ${error.detail.detail}`});
            }
            else            
            {
                setHasArchivePermission(false);
                setErrorMessage({alertType:"danger", message: `Error archiving job: ${error.message}`});
            }
        }       
    })

    const handleDownloadJob = useCallback(async () =>
    {
        const jobId = rowProps.data.id;
        const {downloadId, downloadSignal} = startDownload(jobId, `job-${jobId}.zip`);
        try
        {
            const response = await downloadJob(jobId, updateProgress, downloadId, downloadSignal);
            if (response.status === "error" || response.status === "warning") {
                setErrorMessage({alertType:"danger", message: response.message});
                updateErrorDownloads(response.download_id);
            }
        }
        catch(error)
        {
            setErrorMessage({alertType:"danger", message: `Error downloading job: ${error.message}`});
            updateErrorDownloads(downloadId);
        }
    })

    return(
      <>
        <ButtonGroup size="sm">
          <JobStateAction jobData = {rowProps.data} handleJobStateChange = {handleJobStateChange} hasStateChangePermission = {hasStateChangePermission} onUploadClick = {handleUploadClick} />
          <DownloadJob jobData = {rowProps.data} handleDownloadJob = {handleDownloadJob}/>
          <ArchiveJob jobData = {rowProps.data} handleArchiveJob = {handleArchiveJob} hasArchivePermission = {hasArchivePermission}/>
        </ButtonGroup>
        <FilesUpload show = {showUploadModal} handleClose = {handleUploadClose} jobId = {rowProps.data?.id} refreshFiles = {rowProps.refreshFiles} />
      </>
    )
}


export const JobActions = ({jobDetails, queryClient, id, active}) => {
    const { hasRole } = usePermissionContext();
    const { startDownload, updateProgress, updateErrorDownloads } = useDownloadFileProgress();
    const [showUploadModal, setShowUploadModal] = useState(false);
    const handleUploadClick = useCallback(() => setShowUploadModal(true), []);
    const handleUploadClose = useCallback(() => setShowUploadModal(false), []);

    const handleJobStateChange = useCallback(async (newJobState) => {
        const response = await updateJobState(id, newJobState);
        queryClient.setQueryData(['job', id], oldData => ({
            ...oldData,
            state: (response.state === undefined || response.state === null) ? `${response.detail} State will revert.` : response.state,
        }))
        
        
    })

    const handleArchiveJob = useCallback(async () => 
    {
        try
        {
            const response = await updateJobState(id, "archive");
            queryClient.setQueryData(['job', id], oldData => ({
            ...oldData,
            state: response.state ? response.state : `${response.detail} State will revert.`,
            }))
        }
        catch(error){
            queryClient.setQueryData(['job', id], oldData => ({
            ...oldData,
            state: error.detail? error.detail.detail : 'Error Occurred. Please Wait Or Refresh',
            stateError: true,
            }))
        }
        
    })

    const handlePurgeJob = useCallback(async () =>
    {
        try
        {
            const response = await updateJobState(id, "purge");
            queryClient.setQueryData(['job', id], oldData => ({
                ...oldData,
                state: response.state,
            }))
        }
        catch(error){

        }
        
    })

    const handleDownloadJob = useCallback(async () =>
    {
        const {downloadId, downloadSignal} = startDownload(id, `job-${id}.zip`);
        try
        {
            const response = await downloadJob(id, updateProgress, downloadId, downloadSignal);
            if (response.status === "error" || response.status === "warning") {
                updateErrorDownloads(response.download_id);
            }
        }
        catch(error)
        {
            updateErrorDownloads(downloadId);
        }
    })

    return(
        <>
          <ButtonGroup size="sm">
            <JobStateAction jobData = {jobDetails} handleJobStateChange={handleJobStateChange} hasStateChangePermission = {!active} onUploadClick = {handleUploadClick} />
            <DownloadJob jobData = {jobDetails} handleDownloadJob = {handleDownloadJob}/>
            <ArchiveJob jobData = {jobDetails} handleArchiveJob = {handleArchiveJob} hasArchivePermission={hasRole('Admin')}/>
          </ButtonGroup>
          <FilesUpload show = {showUploadModal} handleClose = {handleUploadClose} jobId = {id} />
        </>
    )
}